import React, { memo, useMemo } from 'react';
import { I18n } from 'react-redux-i18n';
import logoFull from '../../../assets/images/nawaiam-logo.png';
import './IeBody.css';
import { REPORTS_DATA } from './data';

// Componente memoizado para el contenido de cada párrafo
const ContentParagraph = memo(({ content, index }) => (
  <p
    key={`content-${index}`}
    data-pdf-block=""
    style={{ fontSize: '12px', textAlign: 'justify' }}
    dangerouslySetInnerHTML={{ __html: content }}
  />
));

// Componente memoizado para cada sub-item
const SubItem = memo(({ subItem, index }) => (
  <li key={`subitem-${index}`} style={{ marginBottom: '10px' }}>
    <p
      style={{
        margin: '0',
        lineHeight: '1.4',
        fontSize: '12px',
        textAlign: 'justify',
      }}
    >
      <strong style={{ fontWeight: 'bold' }}>{subItem.title}:</strong>{' '}
      <span dangerouslySetInnerHTML={{ __html: subItem.description }} />
    </p>
  </li>
));

// Componente memoizado para cada item de la lista
const ListItem = memo(({ item, index, language }) => {
  return (
    <li
      key={`listitem-${index}`}
      data-pdf-block=""
      style={{ marginBottom: '18px' }}
    >
      <p
        style={{
          fontSize: '12px',
          margin: '0 0 8px 0',
        }}
      >
        <strong style={{ fontWeight: 'bold' }}>{item.title}:</strong>{' '}
        <span dangerouslySetInnerHTML={{ __html: item.description }} />
      </p>
      <ul
        style={{
          listStyleType: language === 'es' ? 'circle' : 'none',
          paddingLeft: language === 'es' ? '18px' : '0',
          margin: '0',
          fontSize: '12px',
        }}
      >
        {item.subList?.map((subItem, subIndex) => (
          <SubItem
            key={`${index}-${subIndex}`}
            subItem={subItem}
            index={subIndex}
          />
        ))}
      </ul>
    </li>
  );
});

// Componente memoizado para cada reporte
const ReportSection = memo(({ report, index, language }) => {
  const isAdditionalRecommendation = [
    'Additional Recommendations',
    'Recomendações Adicionais',
  ].includes(report.title);
  const isGuidanceSupport = [
    'Guidance, Support, and Recommendations',
    'Orientação, Apoio e Recomendações',
  ].includes(report.title);

  const shouldApplyStyles =
    language === 'es' || isAdditionalRecommendation || isGuidanceSupport;

  return (
    <div key={`report-${index}`}>
      <h4
        data-pdf-block=""
        data-pdf-keep-next=""
        style={{ fontSize: '14px', fontWeight: 'bold', marginTop: '20px' }}
      >
        {report.title}
      </h4>

      {/* Renderizar contenido si existe */}
      {report.content?.length > 0 && (
        <div className="report-content">
          {report.content.map((content, contentIndex) => (
            <ContentParagraph
              key={`${index}-content-${contentIndex}`}
              content={content}
              index={contentIndex}
            />
          ))}
        </div>
      )}

      {/* Renderizar lista si existe */}
      {report.list?.length > 0 && (
        <ul
          className="report-list"
          style={{
            listStyleType: shouldApplyStyles ? 'disc' : 'none',
            paddingLeft: shouldApplyStyles ? '16px' : '0',
            fontSize: '12px',
            marginLeft: shouldApplyStyles ? '16px' : '0',
          }}
        >
          {report.list.map((item, listIndex) => (
            <ListItem
              key={`${index}-list-${listIndex}`}
              item={item}
              index={listIndex}
              language={language}
            />
          ))}
        </ul>
      )}
    </div>
  );
});

// Componente principal optimizado
const IeBody = memo(({ name, email, language = 'es', reportId = '9' }) => {
  // Memoizar los datos del reporte para evitar recálculos
  const reportData = useMemo(() => {
    return REPORTS_DATA[language]?.[reportId] || [];
  }, [language, reportId]);

  // Memoizar las secciones de información personal
  const personalDataSection = useMemo(
    () => (
      <section className="personal-data" data-pdf-block="">
        <h4>{I18n.t('persons.personalDetails')}</h4>
        <span>
          {I18n.t('clients.name')}: {name}
        </span>
        <span>
          {I18n.t('clients.email')}: {email}
        </span>
      </section>
    ),
    [name, email],
  );

  return (
    <div className="ie-body-container">
      <header data-pdf-block="">
        <img className="logo-text" src={logoFull} alt="logoText" />
      </header>

      {personalDataSection}

      {/* Renderizar reportes de manera optimizada */}
      <div className="reports-container" style={{ paddingLeft: '16px' }}>
        {reportData.map((report, index) => (
          <ReportSection
            key={`report-section-${index}`}
            report={report}
            index={index}
            language={language}
          />
        ))}
      </div>

      {/* Sección comentada mantenida para referencia futura */}
      {/* <section className="traits">
        <h4>{I18n.t('ieprofile.title2')}</h4>
        <div className="selfknowledge">
          <p className="trait-paragraph">
            <span className="trait-key">
              {I18n.t('ieprofile.selfknowledge.key')}
            </span>
            {I18n.t('ieprofile.selfknowledge.desc')}
          </p>
        </div>
        <div className="selfregulation">
          <p className="trait-paragraph">
            <span className="trait-key">
              {I18n.t('ieprofile.selfregulation.key')}
            </span>
            {I18n.t('ieprofile.selfregulation.desc')}
          </p>
        </div>
        <div className="motivation">
          <p className="trait-paragraph">
            <span className="trait-key">
              {I18n.t('ieprofile.motivation.key')}
            </span>
            {I18n.t('ieprofile.motivation.desc')}
          </p>
        </div>
        <div className="empathy">
          <p className="trait-paragraph">
            <span className="trait-key">{I18n.t('ieprofile.empathy.key')}</span>
            {I18n.t('ieprofile.empathy.desc')}
          </p>
        </div>
        <div className="socialskills">
          <p className="trait-paragraph">
            <span className="trait-key">
              {I18n.t('ieprofile.socialskills.key')}
            </span>
            {I18n.t('ieprofile.socialskills.desc')}
          </p>
        </div>
      </section>
      <section>
        <h4>{I18n.t('ieprofile.title3')}</h4>
        <p>{I18n.t('ieprofile.paragraph3')}</p>
        <p>{I18n.t('ieprofile.paragraph4')}</p>
      </section>
      <section>
        <h4>{I18n.t('ieprofile.title4')}</h4>
        <p>{I18n.t('ieprofile.paragraph5')}</p>
      </section> */}

      <br />
    </div>
  );
});

// Agregar displayName para debugging
IeBody.displayName = 'IeBody';

export default IeBody;
